import React, { useEffect, useState, useCallback } from 'react';
import { Search, RotateCcw, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';

// Components
import StyledHeadingText from '../components/Fonts/StyledHeadingText';
import CustomLoading from '../components/customLoading/CustomLoading';
import Breadcrumbs from '../components/BreadCrumbs';
import ShowModal from '../utils/ShowModal';
import DeviceHistoryModalContent from './DeviceSubscriptionHistory';

// Services
import { getIotDevices } from '../services/iotSubscriptionList';
import { formatDate } from '../utils/FormatDateTime';

const PAGE_SIZE = 10; // Matches backend default limit

const IotSubscriptionPage = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0 });
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeDevice, setActiveDevice] = useState(null);

    // Fetch records now accepts a page parameter
    const fetchRecords = useCallback(async (query = '', page = 1) => {
        setLoading(true);
        try {
            const res = await getIotDevices(query, page);
            if (res.status === 200) {
                setData(res.data);
            }
        } catch (err) {
            toast.error("Could not load records");
        } finally {
            setLoading(false);
        }
    }, []);

    // Re-fetch when search text or page changes
    useEffect(() => {
        fetchRecords(searchText, currentPage);
    }, [fetchRecords, searchText, currentPage]);

    // Reset page to 1 when searching to avoid "no results found" on page 5
    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const handleResetSearch = () => {
        setSearchText('');
        setCurrentPage(1);
    };

    const handleViewHistory = (item) => {
        setActiveDevice(item);
        setIsModalOpen(true);
    };

    const totalPages = Math.ceil(data.count / PAGE_SIZE);

    return (
        <div style={{ padding: '20px' }}>
            <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'IoT Subscriptions' }]} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0' }}>
                <StyledHeadingText text="Subscription Management" />
                
                {/* Search Bar */}
                <div style={searchContainer}>
                    <Search size={18} color="#94a3b8" />
                    <input 
                        style={searchInput}
                        placeholder="Search ID or Owner Number..."
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                    {searchText && (
                        <RotateCcw 
                            size={16} 
                            onClick={handleResetSearch} 
                            style={{ cursor: 'pointer', color: '#64748b' }} 
                        />
                    )}
                </div>
            </div>

            {loading ? <CustomLoading size={60} /> : (
                <>
                    <div style={tableWrapper}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={th}>Device ID</th>
                                    <th style={th}>Owner Number</th>
                                    <th style={th}>Subscription Period</th>
                                    <th style={th}>Status</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.results?.length > 0 ? (
                                    data.results.map((item) => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={td}>
                                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{item.device_id}</div>
                                                <div style={{ fontSize: '11px', color: '#64748b' }}>{item.device_name}</div>
                                            </td>
                                            <td style={td}>{item.owner_number || 'N/A'}</td>
                                            <td style={td}>
                                                <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <div><strong style={{ color: '#64748b' }}>Start:</strong> {item.subscription_start_date ? formatDate(item.subscription_start_date) : 'N/A'}</div>
                                                    <div><strong style={{ color: '#64748b' }}>End:</strong> {item.subscription_end_date ? formatDate(item.subscription_end_date) : 'N/A'}</div>
                                                </div>
                                            </td>
                                            <td style={td}>
                                                <span style={{ 
                                                    padding: '4px 8px', 
                                                    borderRadius: '6px', 
                                                    fontSize: '11px', 
                                                    fontWeight: 700,
                                                    background: item.is_active ? '#dcfce7' : '#fee2e2',
                                                    color: item.is_active ? '#16a34a' : '#dc2626'
                                                }}>
                                                    {item.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                </span>
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                <button 
                                                    onClick={() => handleViewHistory(item)}
                                                    style={historyBtn}
                                                >
                                                    <History size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> History
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                                            No records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {data.count > 0 && (
                        <div style={paginationContainer}>
                            <div style={{ fontSize: '14px', color: '#64748b' }}>
                                Showing <b>{(currentPage - 1) * PAGE_SIZE + 1}</b> to <b>{Math.min(currentPage * PAGE_SIZE, data.count)}</b> of <b>{data.count}</b> entries
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                    style={paginationBtn(currentPage === 1)}
                                >
                                    <ChevronLeft size={16} /> Previous
                                </button>
                                
                                <button 
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    style={paginationBtn(currentPage >= totalPages)}
                                >
                                    Next <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <ShowModal
                contentLabel="History"
                isOpen={isModalOpen}
                modalWidth="50%"
                isSidebar={true}
                onRequestClose={() => setIsModalOpen(false)}
                componentToPass={<DeviceHistoryModalContent device={activeDevice} />}
            />
        </div>
    );
};

// Styles
const th = { padding: '12px 20px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', textAlign: 'left' };
const td = { padding: '16px 20px', fontSize: '14px', color: '#334155' };

const searchContainer = { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '8px', 
    background: '#fff', 
    border: '1px solid #e2e8f0', 
    padding: '6px 12px', 
    borderRadius: '8px' 
};

const searchInput = { border: 'none', outline: 'none', fontSize: '14px', width: '250px' };

const tableWrapper = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' };

const historyBtn = { 
    background: '#f1f5f9', 
    border: 'none', 
    padding: '8px 12px', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '13px', 
    fontWeight: 600, 
    color: '#475569' 
};

const paginationContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    padding: '0 10px'
};

const paginationBtn = (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: disabled ? '#f8fafc' : '#fff',
    color: disabled ? '#cbd5e1' : '#475569',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 600,
    fontSize: '13px',
    transition: 'background 0.2s'
});

export default IotSubscriptionPage;